import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import RoleCard from './components/RoleCard';
import RoleSelector from './components/RoleSelector';
import PermissionMatrix from './components/PermissionMatrix';
import PermissionSummary from './components/PermissionSummary';
import { permissionsService } from '../../services/userManagementService';

const RolePermissionsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole);
    }
  }, [selectedRole]);

  useEffect(() => {
    checkForChanges();
  }, [permissions, originalPermissions]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data } = await permissionsService?.getAll();
      if (data) {
        setPermissions(data);
        setOriginalPermissions(JSON.parse(JSON.stringify(data)));
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (role) => {
    try {
      const { data } = await permissionsService?.getByRole(role);
      if (data) {
        // Update only the selected role's permissions
        setPermissions(prevPermissions => {
          const otherRolePermissions = prevPermissions?.filter(p => p?.role !== role);
          return [...otherRolePermissions, ...data];
        });
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    }
  };

  const checkForChanges = () => {
    const hasChanged = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
    setHasChanges(hasChanged);
  };

  const handleRoleChange = (role) => {
    if (hasChanges) {
      const confirmChange = window.confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirmChange) return;
      // Reset to original permissions
      setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    }
    setSelectedRole(role);
  };

  const handlePermissionChange = (permissionId, updates) => {
    setPermissions(prevPermissions => 
      prevPermissions?.map(perm => 
        perm?.id === permissionId ? { ...perm, ...updates } : perm
      )
    );
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const changedPermissions = permissions?.filter((perm, index) => {
        const original = originalPermissions?.[index];
        return JSON.stringify(perm) !== JSON.stringify(original);
      });

      for (const perm of changedPermissions) {
        await permissionsService?.update(perm?.id, perm);
      }

      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setHasChanges(false);
      alert('Permissions updated successfully!');
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getRolePermissions = (role) => {
    return permissions?.filter(p => p?.role === role);
  };

  const roles = ['admin', 'analyst', 'merchant'];
  const selectedRolePermissions = getRolePermissions(selectedRole);

  return (
    <>
      <Helmet>
        <title>Role & Permissions Management | PaymentGateway</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Role & Permissions Management</h1>
            <p className="text-muted-foreground">Configure access rights and feature permissions for each user role</p>
          </div>

          {/* Role Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {roles?.map((role) => (
              <RoleCard
                key={role}
                role={role}
                permissions={getRolePermissions(role)}
                onSelect={() => handleRoleChange(role)}
                isSelected={selectedRole === role}
              />
            ))}
          </div>

          {/* Role Selector */}
          <div className="mb-6">
            <RoleSelector
              selectedRole={selectedRole}
              onRoleChange={handleRoleChange}
              onSaveChanges={handleSaveChanges}
              hasChanges={hasChanges}
              saving={saving}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Permission Matrix */}
            <div className="lg:col-span-2">
              <PermissionMatrix
                permissions={selectedRolePermissions}
                onPermissionChange={handlePermissionChange}
                loading={loading}
              />
            </div>

            {/* Permission Summary */}
            <div className="lg:col-span-1">
              <PermissionSummary
                permissions={selectedRolePermissions}
                selectedRole={selectedRole}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default RolePermissionsManagement;